import { useMemo } from "react";

export const usePayShareFeeCalculation = (feetypes: any[]) => {
  // Helper function to get fee and share information from student data
  const getFeeAndShareInfo = (studentData: any, feeType: string) => {
    if (!studentData || !feeType) {
      return { feeAmount: 0, shareAmount: 0 };
    }

    // Get fee type by ID from API response
    const feeTypeObj = feetypes.find((ft) => ft.id.toString() === feeType);
    const feeTypeCode = feeTypeObj?.type || "";

    // Check if the student has center and reg_fee_share data
    if (feeType === "2" || feeTypeCode === "registrationfee") {
      // For registration fee, get data from reg_fee_share
      if (
        studentData.center?.reg_fee_share &&
        studentData.center.reg_fee_share.length > 0
      ) {
        const regFeeShare = studentData.center.reg_fee_share.find(
          (fee: any) => fee.course_id === studentData.course_id
        );

        if (regFeeShare) {
          const feeAmount = regFeeShare.reg_fee || 0;
          let shareAmount = 0;

          // Calculate share based on share_type
          if (regFeeShare.share_type === "fixed") {
            shareAmount = regFeeShare.share_amount || 0;
          } else if (regFeeShare.share_type === "percent") {
            shareAmount = Math.round(
              feeAmount * (regFeeShare.share_amount / 100)
            );
          }

          return {
            feeAmount,
            shareAmount,
            paymentStatus: "pending",
            paymentDate: new Date().toISOString().split("T")[0],
          };
        }
      }
    }

    // Check if the student has invoices
    if (studentData.invoices && studentData.invoices.length > 0) {
      // Find the invoice matching the fee type
      const matchingInvoice = studentData.invoices.find((invoice: any) => {
        if (feeType === "2" || feeTypeCode === "registrationfee") {
          return invoice.type === "registrationfee";
        } else if (feeType === "1" || feeTypeCode === "coursefee") {
          return (
            invoice.type === "coursefee" &&
            (!studentData.selectedStage || invoice.stage_id === Number(studentData.selectedStage))
          );
        }
        return false;
      });

      if (matchingInvoice) {
        const feeAmount = matchingInvoice.payable || 0;

        // Calculate share amount from centerPayments if available
        if (
          studentData.centerPayments &&
          studentData.centerPayments.length > 0
        ) {
          const matchingPayment = studentData.centerPayments.find(
            (payment: any) => payment.invoice_id === matchingInvoice.id
          );

          if (matchingPayment) {
            return {
              feeAmount,
              shareAmount: matchingPayment.amount || 0,
              paymentStatus: matchingPayment.status || "pending",
              paymentDate:
                matchingPayment.created_at?.split("T")[0] ||
                new Date().toISOString().split("T")[0],
            };
          }
        }

        // If no payment data, calculate estimated share based on center data
        const shareAmount =
          studentData.center?.course_fee_share?.length > 0 ||
          studentData.center?.reg_fee_share?.length > 0
            ? calculateEstimatedShare(studentData, feeType, feeAmount)
            : Math.round(feeAmount * 0.5); // Default to 50% if no share data

        return {
          feeAmount,
          shareAmount,
          paymentStatus: "pending",
          paymentDate: new Date().toISOString().split("T")[0],
        };
      }
    }

    return {
      feeAmount: 0,
      shareAmount: 0,
      paymentStatus: "pending",
      paymentDate: new Date().toISOString().split("T")[0],
    };
  };

  // Helper function to calculate estimated share amount from center data
  const calculateEstimatedShare = (
    studentData: any,
    feeType: string,
    feeAmount: number
  ) => {
    const feeTypeObj = feetypes.find((ft) => ft.id.toString() === feeType);
    const feeTypeCode = feeTypeObj?.type || "";
    const center = studentData.center;

    if (!center) return Math.round(feeAmount * 0.5); // Default 50% if no center

    if (feeType === "2" || feeTypeCode === "registrationfee") {
      // Registration fee
      if (center.reg_fee_share && center.reg_fee_share.length > 0) {
        const regFeeShare = center.reg_fee_share.find(
          (fee: any) => fee.course_id === studentData.course_id
        );

        if (regFeeShare) {
          if (regFeeShare.share_type === "fixed") {
            return regFeeShare.share_amount;
          } else if (regFeeShare.share_type === "percent") {
            // If fee amount is passed, use it, otherwise use reg_fee from share data
            const baseAmount = feeAmount > 0 ? feeAmount : regFeeShare.reg_fee;
            return Math.round(baseAmount * (regFeeShare.share_amount / 100));
          }
        }
      }
    } else if (feeType === "1" || feeTypeCode === "coursefee") {
      // Course fee
      if (center.course_fee_share && center.course_fee_share.length > 0) {
        const courseFeeShare = center.course_fee_share.find(
          (fee: any) => fee.course_id === studentData.course_id
        );

        if (courseFeeShare && courseFeeShare.stageFeeShare) {
          const stageFeeShare = courseFeeShare.stageFeeShare.find(
            (stage: any) => stage.stage_id === studentData.stage_id
          );

          if (stageFeeShare) {
            if (courseFeeShare.share_type === "fixed") {
              return stageFeeShare.share_amount;
            } else if (courseFeeShare.share_type === "percent") {
              // If fee amount is passed, use it, otherwise use stage_fee from share data
              const baseAmount =
                feeAmount > 0 ? feeAmount : stageFeeShare.stage_fee;
              return Math.round(
                baseAmount * (stageFeeShare.share_amount / 100)
              );
            }
          }
        }
      }
    }

    return Math.round(feeAmount * 0.5); // Default to 50% share
  };

  return {
    getFeeAndShareInfo,
    calculateEstimatedShare,
  };
}; 